import React, { useCallback, useEffect, useRef } from 'react';
import { Dimensions, View, Animated, Easing, Platform } from 'react-native';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import Text from '../v2BaseComponents/Text';
import { colors, zIndex } from '../../utils/theme';
import { NotificationPortalContainer } from '../v2BaseComponents/FixedPositionPortalContainer';
import { TAB_BAR_HEIGHT } from '../AppNavigator/TabNavigation/constants';
import { ACTION_BUTTON_LAYOUT_ACTION_BAR_HEIGHT } from '../v2BaseComponents/ActionButtonLayout/ActionButtonLayout';
import { useForceUpdate } from '../../hooks/useForceUpdate';
import { mainContentPadding } from '../v2BaseComponents/MainContentContainer';
import PressableDynamicOpacity from '../v2BaseComponents/PressableDynamicOpacity';

export interface NotificationProviderOwnProps {
    children: any;
}

export type NotificationProviderProps = NotificationProviderOwnProps;

export type NotificationSeverity = 'error' | 'success' | 'warn';
export type NotificationEvent = {
    message: string;
    severity: NotificationSeverity;
};

export type NotifyFunc = (severity: NotificationSeverity, message: string) => void;

export type NotificationContextValue = {
    notify: NotifyFunc;
};

export const NotificationContext = React.createContext<NotificationContextValue>({
    notify: () => {
        return;
    },
});

const NOTIFICATION_TIMEOUT = 5000;

const Notification: React.FC<NotificationEvent & { onClose: () => void }> = ({ message, onClose, severity }) => {
    const [internalIsShown, setInternalIsShown] = React.useState(true);
    // track a mount delay to help with e2e capturing the notification before animation completes
    const [hasMountDelayPassed, setHasMountDelayPassed] = React.useState(false);
    const presenceAnimationRef = useRef(new Animated.Value(0));
    const opacityAnimationRef = useRef(new Animated.Value(0));
    useEffect(() => {
        const timeout = setTimeout(() => {
            setInternalIsShown(false);
        }, NOTIFICATION_TIMEOUT);

        return () => {
            clearTimeout(timeout);
        };
    }, []);

    useEffect(() => {
        Animated.parallel([
            Animated.timing(presenceAnimationRef.current, {
                toValue: internalIsShown ? 1 : 0,
                duration: 300,
                useNativeDriver: Platform.OS === 'web',
                easing: Easing.inOut(Easing.ease),
            }),
            Animated.timing(opacityAnimationRef.current, {
                toValue: internalIsShown ? 1 : 0,
                duration: 200,
                useNativeDriver: true,
                delay: 100,
            }),
        ]).start((result) => {
            if (result.finished) {
                if (!internalIsShown) {
                    onClose();
                } else {
                    setHasMountDelayPassed(true);
                }
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internalIsShown]);

    const screenLayoutPadding = 2;
    const isMobileLayout = useIsMobileLayout();
    const baseTestId = `notification-${severity}-${message}`;
    const backgroundColor =
        severity === 'error' ? colors.Alert : severity === 'success' ? colors.Success : colors['Primary-Reg'];
    const desktopWidth = 400;
    const notificationWidth = isMobileLayout ? Dimensions.get('window').width * 0.9 : desktopWidth;
    const notificationHeight = ACTION_BUTTON_LAYOUT_ACTION_BAR_HEIGHT - screenLayoutPadding * 2;

    return (
        <NotificationPortalContainer>
            <Animated.View
                style={[
                    {
                        zIndex: zIndex.notification,
                        position: 'absolute',
                        flexDirection: 'row',
                        opacity: presenceAnimationRef.current,
                        overflow: 'hidden',
                        minHeight: notificationHeight,
                        borderRadius: 5,
                    },
                    isMobileLayout
                        ? {
                              bottom: TAB_BAR_HEIGHT + screenLayoutPadding,
                              justifyContent: 'center',
                              width: Dimensions.get('window').width,
                              left: 0,
                          }
                        : {
                              bottom: ACTION_BUTTON_LAYOUT_ACTION_BAR_HEIGHT + screenLayoutPadding * 2,
                              width: notificationWidth + mainContentPadding,
                              left: Dimensions.get('window').width - (notificationWidth + mainContentPadding),
                          },
                ]}
            >
                <Animated.View
                    style={[
                        { bottom: 0, position: 'absolute', width: notificationWidth, height: '100%' },
                        isMobileLayout
                            ? {
                                  right: presenceAnimationRef.current.interpolate({
                                      inputRange: [0, 1],
                                      outputRange: [
                                          -1 * notificationWidth,
                                          (Dimensions.get('window').width - notificationWidth) / 2,
                                      ],
                                  }),
                              }
                            : {
                                  right: presenceAnimationRef.current.interpolate({
                                      inputRange: [0, 1],
                                      outputRange: [-1 * notificationWidth, mainContentPadding],
                                  }),
                              },
                    ]}
                >
                    <PressableDynamicOpacity
                        onPress={() => {
                            setInternalIsShown(false);
                        }}
                        style={{
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <View
                            style={[
                                {
                                    zIndex: zIndex.notification,
                                    backgroundColor,
                                    borderRadius: 5,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    height: '100%',
                                },
                            ]}
                            testID={`${baseTestId}-${hasMountDelayPassed ? 'mounted' : 'unmounted'}`}
                        >
                            <Text variant="notification-title" alignText="center">
                                {message}
                            </Text>
                        </View>
                    </PressableDynamicOpacity>
                </Animated.View>
            </Animated.View>
        </NotificationPortalContainer>
    );
};

type PendingNotification = {
    severity: NotificationSeverity;
    message: string;
    dateTime: string;
};

const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }: NotificationProviderProps) => {
    const { forceUpdate } = useForceUpdate();
    const pendingNotifications = React.useRef<PendingNotification[]>([]);
    const pendingNotification = pendingNotifications.current[pendingNotifications.current.length - 1];

    const notify = useCallback<NotifyFunc>(
        (severity, message) => {
            pendingNotifications.current.push({ severity, message, dateTime: new Date().toISOString() });
            forceUpdate();
        },
        [forceUpdate],
    );

    const contextValue = React.useMemo(() => ({ notify }), [notify]);

    const renderNotification = () => {
        if (!pendingNotification) return null;

        return (
            <Notification
                message={pendingNotification.message}
                onClose={() => {
                    pendingNotifications.current.pop();
                    forceUpdate();
                }}
                severity={pendingNotification.severity}
                key={pendingNotification.message + pendingNotification.dateTime}
            />
        );
    };

    return (
        <>
            <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
            {renderNotification()}
        </>
    );
};

export default React.memo(NotificationProvider);
